import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import Reveal from "../components/Reveal";
import { SERVICES } from "../data/projects";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIMELINES = ["ASAP", "1–3 months", "3–6 months", "Just planning"];
const PROPERTY_TYPES = ["House", "Condo / Apartment", "Townhome", "Commercial"];

const inputCls =
  "w-full rounded-xl border border-line bg-ink px-4 py-3.5 text-sm text-cream placeholder:text-cream/35 outline-none transition-colors duration-300 focus:border-copper";

export default function EnquirySection() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "kitchen",
    budget: 120,
    timeline: TIMELINES[1],
    property_type: PROPERTY_TYPES[0],
    message: "",
  });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const handler = (e) => setForm((f) => ({ ...f, service: e.detail }));
    window.addEventListener("preselect-service", handler);
    return () => window.removeEventListener("preselect-service", handler);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, budget: Number(form.budget) * 1000 });
      toast.success("Enquiry received. Our studio will reply within one business day.");
      setForm((f) => ({ ...f, name: "", email: "", phone: "", message: "" }));
    } catch (err) {
      toast.error("Could not send your enquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="enquiry-section" className="relative overflow-hidden border-t border-line bg-surface/40">
      <div className="hero-glow pointer-events-none absolute inset-0" />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-14 px-4 py-24 sm:px-8 lg:grid-cols-2 lg:py-32">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-copper">Start a project</p>
          <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight text-cream sm:text-5xl">
            Tell us what you want to rebuild.
          </h2>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-cream/60">
            Share the essentials and our studio responds within one business
            day with next steps, a site-visit window, and an honest read on
            feasibility.
          </p>
          <div className="mt-10 space-y-3 font-mono text-xs uppercase tracking-[0.2em] text-cream/50">
            <p>build@jobaconstruction.com</p>
            <p>+1 (267) 346-8487 · +1 (215) 281-8089 · +1 (267) 745-1243</p>
            <p>Mon–Fri · 8:00–18:00 · Philadelphia, PA</p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            data-testid="enquiry-form"
            onSubmit={submit}
            className="flex flex-col gap-5 rounded-2xl border border-line bg-surface p-6 sm:p-8"
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <input
                data-testid="enquiry-form-name"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Full name"
                className={inputCls}
              />
              <input
                data-testid="enquiry-form-email"
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="Email"
                className={inputCls}
              />
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <input
                data-testid="enquiry-form-phone"
                value={form.phone}
                onChange={set("phone")}
                placeholder="Phone (optional)"
                className={inputCls}
              />
              <select
                data-testid="enquiry-form-service-select"
                value={form.service}
                onChange={set("service")}
                className={inputCls}
              >
                {SERVICES.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.short}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream/50">
                  Investment range
                </label>
                <span data-testid="enquiry-form-budget-value" className="font-mono text-sm font-semibold text-copper">
                  {form.budget >= 250 ? "$250k+" : `$${form.budget}k`}
                </span>
              </div>
              <input
                data-testid="enquiry-form-budget-slider"
                type="range"
                min="25"
                max="250"
                step="5"
                value={form.budget}
                onChange={set("budget")}
                className="w-full accent-copper"
              />
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <select
                data-testid="enquiry-form-timeline"
                value={form.timeline}
                onChange={set("timeline")}
                className={inputCls}
              >
                {TIMELINES.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
              <select
                data-testid="enquiry-form-property-type"
                value={form.property_type}
                onChange={set("property_type")}
                className={inputCls}
              >
                {PROPERTY_TYPES.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </div>

            <textarea
              data-testid="enquiry-form-message"
              rows={4}
              value={form.message}
              onChange={set("message")}
              placeholder="Tell us about your space and what you want changed…"
              className={`${inputCls} resize-none`}
            />

            <button
              data-testid="enquiry-form-submit-button"
              type="submit"
              disabled={sending}
              className="group mt-1 flex items-center justify-center gap-2 rounded-full bg-copper px-7 py-4 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors duration-300 hover:bg-copper-hover disabled:opacity-60"
            >
              {sending ? "Sending…" : "Send enquiry"}
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
